import React, { useState, useCallback, useEffect } from "react";
import { Box } from "@mui/material";
import sxClasses from "../../flowdesigner.styles";
import SentinelLiveMonitor from "src/modules/ai-insights/sentinel-live-monitor/sentinel-live-monitor";
import { useAppDispatch, useAppSelector } from "src/stores/redux/hooks";
import {
    getAppModel,
    getAppStatus,
    getNamespace,
    getNodes,
    getSentinelLiveMonitorDefaultSentinel
} from "../../helpers/selectors";
import {
    setIsSentinelLiveMonitorSidebarOpenAction,
    setSentinelLiveMonitorDefaultSentinelAction,
    setSelectedNodeAction,
    setIsRightSidebarOpenAction
} from "src/stores/redux/features/flowdesigner/flowdesigner.slice";
import { navigateToNode } from "../../helpers/navigation.utils";

type Props = { appName?: string };

export const SentinelLiveMonitorWrapper = ({ appName }: Props) => {
    const dispatch = useAppDispatch();

    const [sentinels, setSentinels] = useState<any[] | null>(null);
    const [fullScreen, setFullScreen] = useState(false);

    const appModel = useAppSelector(getAppModel);
    const appStatus = useAppSelector(getAppStatus);
    const namespace = useAppSelector(getNamespace);
    const sentinelLiveMonitorDefaultSentinel = useAppSelector(getSentinelLiveMonitorDefaultSentinel);
    const nodes = useAppSelector(getNodes);

    useEffect(() => {
        setSentinels(null); // reset to loading state on appModel change
        if (appModel && appModel.getApplicationComponentsByType) {
            appModel
                .getApplicationComponentsByType("SENTINEL")
                .then((sentinelCollection: any) => {
                    setSentinels(sentinelCollection?.models || []);
                })
                .catch((error: any) => {
                    console.error("Failed to fetch sentinels:", error);
                    setSentinels([]);
                });
            return;
        }

        setSentinels([]);
    }, [appModel]);

    const handleCloseSentinelLiveMonitor = useCallback(() => {
        dispatch(setIsSentinelLiveMonitorSidebarOpenAction(false));
        dispatch(setSentinelLiveMonitorDefaultSentinelAction(null));
        setFullScreen(false);
    }, [dispatch]);

    const handleViewSettingsClick = useCallback(
        (sentinelFQN: string) => {
            // Close live monitor
            dispatch(setIsSentinelLiveMonitorSidebarOpenAction(false));
            dispatch(setSentinelLiveMonitorDefaultSentinelAction(null));
            setFullScreen(false);

            // Find the sentinel node in the flow by short name
            const sentinelShortName = sentinelFQN.split(".").pop();
            const sentinelNode = nodes.find(
                node => node.data?.componentType === "SENTINEL" && node.data?.name === sentinelShortName
            );

            if (sentinelNode) {
                dispatch(setSelectedNodeAction(sentinelNode));
                dispatch(setIsRightSidebarOpenAction(true));
                navigateToNode(undefined, appName, sentinelShortName);
            }
        },
        [dispatch, nodes, appName]
    );

    // Wait until sentinels are fetched before mounting SentinelLiveMonitor.
    if (!appModel || sentinels === null) return null;

    return (
        <Box sx={{ width: fullScreen ? "calc(100% - 69px)" : "400px" }}>
            <Box sx={[sxClasses.rightSidebarWrapper, fullScreen ? { maxWidth: "none" } : {}]} id="rightSidebarWrapper">
                <Box sx={sxClasses.piiSidebar} id="piiSidebar">
                    <SentinelLiveMonitor
                        defaultSentinelSelected={sentinelLiveMonitorDefaultSentinel}
                        sentinelsInApp={sentinels}
                        flowStatus={appStatus || "CREATED"}
                        appName={appName?.split(".").pop() || ""}
                        namespace={namespace || ""}
                        onToggleFullScreen={() => setFullScreen(value => !value)}
                        onClose={handleCloseSentinelLiveMonitor}
                        onViewSettingsClick={handleViewSettingsClick}
                    />
                </Box>
            </Box>
        </Box>
    );
};
